// src/pages/Orders/NewDirectShipment.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, onSnapshot, query, serverTimestamp, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import useCustomerPricing from '../../hooks/useCustomerPricing';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '24px',
        maxWidth: '1200px',
        margin: '0 auto',
        color: '#1e293b'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0
    },
    subtitle: {
        color: '#64748b',
        marginTop: '4px',
        fontSize: '14px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#64748b',
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        borderRadius: '6px',
        transition: 'all 0.2s',
        background: '#f1f5f9'
    },
    card: {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e2e8f0',
        padding: '24px',
        marginBottom: '24px'
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#0f172a',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        position: 'relative'
    },
    label: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#475569'
    },
    input: {
        padding: '10px 12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '14px',
        color: '#0f172a',
        width: '100%',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s'
    },
    tableInput: {
        padding: '6px 8px',
        borderRadius: '6px',
        border: '1px solid #e2e8f0',
        fontSize: '13px',
        color: '#0f172a',
        width: '80px',
        textAlign: 'center',
        outline: 'none'
    },
    select: {
        padding: '10px 12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '14px',
        color: '#0f172a',
        width: '100%',
        background: 'white',
        outline: 'none'
    },
    suggestions: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        marginTop: '4px',
        maxHeight: '300px',
        overflowY: 'auto',
        zIndex: 50,
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    },
    suggestionItem: {
        padding: '8px 12px',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#334155',
        transition: 'all 0.1s',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    button: {
        padding: '10px 20px',
        borderRadius: '10px',
        border: 'none',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px'
    },
    primaryButton: {
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        color: 'white',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
    },
    addButton: {
        background: 'linear-gradient(135deg, #10b981, #059669)',
        color: 'white',
        height: '42px',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
    },
    deleteButton: {
        background: '#fee2e2',
        color: '#ef4444',
        padding: '8px',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        '&:hover': {
            background: '#fecaca'
        }
    },
    tableContainer: {
        border: '1px solid #e2e8f0',
        borderRadius: '14px',
        overflow: 'hidden',
        marginTop: '24px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px'
    },
    th: {
        background: '#f8fafc',
        padding: '14px 16px',
        textAlign: 'left',
        color: '#64748b',
        fontWeight: '600',
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #e2e8f0'
    },
    td: {
        padding: '14px 16px',
        borderBottom: '1px solid #f1f5f9',
        color: '#334155',
        verticalAlign: 'middle'
    },
    productImg: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        objectFit: 'cover',
        background: '#f1f5f9',
        display: 'block'
    },
    placeholderImg: {
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        background: '#f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        color: '#94a3b8'
    }
};

function NewDirectShipment() {
    const { currentUser, userData } = useAuth();
    const { customerPrices, getCustomerProductPrice, getEffectivePrice } = useCustomerPricing(userData?.companyId);
    const navigate = useNavigate();

    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [customerNameInput, setCustomerNameInput] = useState('');
    const [shipmentDate, setShipmentDate] = useState(new Date().toISOString().split('T')[0]);
    const [cargoCompany, setCargoCompany] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [lineItems, setLineItems] = useState([]);
    const [currentItem, setCurrentItem] = useState({ productId: '', productName: '', quantity: 1, price: 0, imageUrl: '', warehouseId: '' });
    const [companyInfo, setCompanyInfo] = useState(null);
    const [createInvoice, setCreateInvoice] = useState(true); // Automatically create invoice
    const [vatRatePercent, setVatRatePercent] = useState(20);

    useEffect(() => {
        if (!userData?.companyId) return;
        const companyId = userData.companyId;
        const unsubCustomers = onSnapshot(query(collection(db, 'companies', companyId, 'customers')), snap => setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubProducts = onSnapshot(query(collection(db, 'companies', companyId, 'products')), snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubWarehouses = onSnapshot(query(collection(db, 'companies', companyId, 'warehouses')), snap => setWarehouses(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubCompany = onSnapshot(doc(db, 'companies', companyId), snap => setCompanyInfo(snap.data()));
        return () => { unsubCustomers(); unsubProducts(); unsubWarehouses(); unsubCompany(); };
    }, [userData]);

    const handleCustomerSelect = (customer) => {
        setCustomerNameInput(customer.name);
        setSelectedCustomerId(customer.id);
    };

    const handleProductSelect = (product) => {
        const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
        const effectivePrice = getEffectivePrice(
            selectedCustomerId,
            product.id,
            product,
            selectedCustomer?.customerGroupId
        );

        // Find which warehouses have this product in stock
        const productWarehouses = Object.entries(product.warehouseQuantities || {})
            .filter(([id, qty]) => qty > 0)
            .map(([id, _]) => id);
        
        // If it's only in 1 warehouse, auto-select it. Otherwise leave blank.
        const autoWarehouseId = productWarehouses.length === 1 ? productWarehouses[0] : '';

        setCurrentItem({
            productId: product.id,
            productName: product.name,
            quantity: 1,
            price: effectivePrice.price,
            availableStock: product.quantity || 0,
            imageUrl: product.imageUrl || '',
            warehouseId: autoWarehouseId
        });
    };

    const handleAddItem = () => {
        if (!currentItem.productId || currentItem.quantity <= 0) {
            alert("Lütfen bir ürün seçin ve miktar girin.");
            return;
        }

        if (!currentItem.warehouseId) {
            alert("Lütfen bu ürünün çıkış yapılacağı depoyu seçin.");
            return;
        }

        setLineItems(prev => {
            const existingItemIndex = prev.findIndex(item => item.productId === currentItem.productId && item.warehouseId === currentItem.warehouseId);
            if (existingItemIndex !== -1) {
                const newItems = [...prev];
                const existingItem = newItems[existingItemIndex];
                const updatedQuantity = existingItem.quantity + currentItem.quantity;
                newItems[existingItemIndex] = {
                    ...existingItem,
                    quantity: updatedQuantity,
                    total: updatedQuantity * existingItem.price
                };
                return newItems;
            }
            return [...prev, { ...currentItem, total: currentItem.quantity * currentItem.price }];
        });
        
        setCurrentItem({ productId: '', productName: '', quantity: 1, price: 0, imageUrl: '', warehouseId: '' });
    };

    const updateLineItem = (index, field, value) => {
        setLineItems(prev => {
            const newItems = [...prev];
            const item = { ...newItems[index], [field]: Number(value) };
            item.total = item.quantity * item.price;
            newItems[index] = item;
            return newItems;
        });
    };

    const handleSave = async () => {
        if (!selectedCustomerId || lineItems.length === 0) {
            alert("Lütfen müşteri seçin ve en az bir ürün ekleyin.");
            return;
        }

        const companyId = userData.companyId;
        const customer = customers.find(c => c.id === selectedCustomerId);
        const batch = writeBatch(db);

        try {
            let subTotal = lineItems.reduce((acc, item) => acc + item.total, 0);
            let vatRate = vatRatePercent / 100;
            let vatAmount = subTotal * vatRate;
            let grandTotal = subTotal + vatAmount;

            // 1. Create Order record
            const orderRef = doc(collection(db, 'companies', companyId, 'orders'));
            const orderId = orderRef.id;
            const orderNumber = orderId.substring(0, 6).toUpperCase();

            const orderData = {
                customerId: customer.id,
                customerName: customer.name,
                customerPhone: customer.phone || '',
                customerAddress: customer.address || '',
                orderDate: new Date(shipmentDate),
                lineItems: lineItems.map(({ productName, availableStock, imageUrl, ...rest }) => rest),
                subTotal: subTotal,
                vatRate: vatRate,
                vatAmount: vatAmount,
                total: grandTotal,
                status: 'Sevk Edildi',
                type: 'direct_shipment',
                orderNumber,
                currency: customer.currency || 'TRY',
                exchangeRate: 1, 
                totalTRY: grandTotal,
                createdAt: serverTimestamp()
            };
            batch.set(orderRef, orderData);

            // 2. Create Shipment record
            const shipmentRef = doc(collection(db, 'companies', companyId, 'orders', orderId, 'shipments'));
            const shipmentData = {
                shipmentDate: new Date(shipmentDate),
                cargoCompany,
                trackingNumber,
                items: lineItems.map(item => ({
                    productId: item.productId,
                    name: item.productName,
                    quantity: item.quantity,
                    price: item.price,
                    warehouseId: item.warehouseId
                })),
                status: createInvoice ? 'Faturalandı' : 'Gönderildi',
                createdAt: serverTimestamp()
            };
            if (createInvoice) {
                shipmentData.invoicedAt = serverTimestamp();
            }
            batch.set(shipmentRef, shipmentData);

            if (createInvoice) {
                // Faturayı kaydet
                const invoiceRef = doc(collection(db, 'companies', companyId, 'invoices'));
                const invoiceId = invoiceRef.id;
                batch.set(invoiceRef, {
                    customerId: customer.id,
                    customerName: customer.name,
                    invoiceDate: new Date(shipmentDate),
                    lineItems: lineItems.map(item => ({
                        productId: item.productId,
                        name: item.productName,
                        quantity: item.quantity,
                        price: item.price,
                        total: item.total,
                        warehouseId: item.warehouseId
                    })),
                    vatRate: vatRate,
                    subTotal: subTotal,
                    vatAmount: vatAmount,
                    total: grandTotal,
                    currency: 'TRY',
                    status: 'Oluşturuldu',
                    createdAt: serverTimestamp()
                });

                // Shipment'a invoiceId geri yaz
                batch.update(shipmentRef, { invoiceId: invoiceId });

                // Müşteri bakiyesini güncelle (Satış olduğu için bakiye AZALIR - borçlanır)
                const currentBalance = customer.balance || 0;
                let balanceToSubtract = grandTotal;
                if (customer.currency === 'USD') {
                    // USD müşteriler için grandTotal (TRY) USD'ye çevrilmeli
                    const usdRate = companyInfo?.currencySettings?.USD || 32;
                    balanceToSubtract = grandTotal / usdRate; 
                }
                const newBalance = currentBalance - balanceToSubtract;
                batch.update(doc(db, 'companies', companyId, 'customers', customer.id), { balance: newBalance });

                // Bakiye geçmişi kaydı oluştur (mutabakat raporları için)
                const balanceHistoryRef = doc(collection(db, 'companies', companyId, 'customers', customer.id, 'balanceHistory'));
                batch.set(balanceHistoryRef, {
                    date: serverTimestamp(),
                    type: 'Fatura',
                    description: `Direkt sevkiyattan fatura oluşturuldu (#${orderNumber})`,
                    amount: balanceToSubtract,
                    previousBalance: currentBalance,
                    newBalance: newBalance,
                    invoiceId: invoiceId
                });
            }

            // 3. Stokları güncelle (Depo bazlı)
            const productUpdates = {};
            lineItems.forEach(item => {
                if (!productUpdates[item.productId]) {
                    productUpdates[item.productId] = { totalDeduction: 0, warehouseDeductions: {} };
                }
                productUpdates[item.productId].totalDeduction += item.quantity;
                
                if (!productUpdates[item.productId].warehouseDeductions[item.warehouseId]) {
                    productUpdates[item.productId].warehouseDeductions[item.warehouseId] = 0;
                }
                productUpdates[item.productId].warehouseDeductions[item.warehouseId] += item.quantity;
            });

            Object.keys(productUpdates).forEach(productId => {
                const updateInfo = productUpdates[productId];
                const currentProduct = products.find(p => p.id === productId);
                const productRef = doc(db, 'companies', companyId, 'products', productId);
                
                const updates = {};
                updates.quantity = (currentProduct.quantity || 0) - updateInfo.totalDeduction;
                
                Object.keys(updateInfo.warehouseDeductions).forEach(wId => {
                    const currentWQty = currentProduct.warehouseQuantities?.[wId] || 0;
                    updates[`warehouseQuantities.${wId}`] = currentWQty - updateInfo.warehouseDeductions[wId];
                });
                
                batch.update(productRef, updates);
            });

            // 4. Activity Log
            const logRef = doc(collection(db, 'companies', companyId, 'activityLog'));
            batch.set(logRef, {
                userId: currentUser.uid,
                userEmail: currentUser.email,
                action: createInvoice ? 'Direkt sevk irsaliyesi ve fatura oluşturdu' : 'Direkt sevk irsaliyesi oluşturdu',
                details: { orderNumber, customerName: customer.name },
                timestamp: serverTimestamp()
            });

            await batch.commit();
            alert(createInvoice ? "İrsaliye ve fatura başarıyla oluşturuldu." : "İrsaliye başarıyla oluşturuldu.");
            navigate('/siparisler/tum-sevkiyatlar');
        } catch (error) {
            console.error("Save error:", error);
            alert("Kaydedilirken bir hata oluştu.");
        }
    };

    const filteredCustomers = customerNameInput ? customers.filter(c => c.name.toLowerCase().includes(customerNameInput.toLowerCase())) : [];
    const filteredProducts = currentItem.productName ? products.filter(p => p.name.toLowerCase().includes(currentItem.productName.toLowerCase()) || (p.barcode && p.barcode.includes(currentItem.productName))) : [];

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Yeni Direkt Sevk İrsaliyesi</h1>
                    <p style={styles.subtitle}>Fatura veya sipariş kaydı dâhilinde anlık sevkiyat oluşturun</p>
                </div>
                <Link to="/siparisler/tum-sevkiyatlar" style={styles.backLink}>← Listeye Dön</Link>
            </div>

            <div style={styles.card}>
                <h3 style={styles.sectionTitle}><span>👤</span> Müşteri ve Gönderi Bilgileri</h3>
                <div style={styles.grid}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Müşteri Ara</label>
                        <input
                            type="text"
                            placeholder="Müşteri adını girin..."
                            style={styles.input}
                            value={customerNameInput}
                            onChange={e => { setCustomerNameInput(e.target.value); setSelectedCustomerId(''); }}
                        />
                        {filteredCustomers.length > 0 && customerNameInput && !selectedCustomerId && (
                            <div style={styles.suggestions}>
                                {filteredCustomers.map(c => (
                                    <div key={c.id} style={styles.suggestionItem} onMouseDown={() => handleCustomerSelect(c)}>
                                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#64748b' }}>
                                            {c.name.substring(0, 1).toUpperCase()}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '600' }}>{c.name}</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{c.phone || 'Telefon Yok'}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Sevk Tarihi</label>
                        <input style={styles.input} type="date" value={shipmentDate} onChange={e => setShipmentDate(e.target.value)} />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Kargo Firması</label>
                        <input style={styles.input} type="text" value={cargoCompany} onChange={e => setCargoCompany(e.target.value)} placeholder="Örn: Aras Kargo" />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Takip No</label>
                        <input style={styles.input} type="text" value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} placeholder="Kargo takip numarası" />
                    </div>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '32px 0' }} />

                <h3 style={styles.sectionTitle}><span>📦</span> Ürün Ekle</h3>
                <div style={{ ...styles.grid, gridTemplateColumns: 'min-content 3fr 1fr 1fr 1fr auto', alignItems: 'flex-end', gap: '16px' }}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Önizleme</label>
                        {currentItem.imageUrl ? (
                            <img src={currentItem.imageUrl} alt="" style={styles.productImg} onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                            <div style={styles.placeholderImg}>🖼️</div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Ürün Ara</label>
                        <input
                            type="text"
                            placeholder="Ürün adı veya barkod..."
                            style={styles.input}
                            value={currentItem.productName}
                            onChange={e => setCurrentItem(prev => ({ ...prev, productName: e.target.value, productId: '' }))}
                        />
                        {filteredProducts.length > 0 && currentItem.productName && !currentItem.productId && (
                            <div style={styles.suggestions}>
                                {filteredProducts.map(p => {
                                    const isAdded = lineItems.some(item => item.productId === p.id);
                                    return (
                                        <div key={p.id} style={{ ...styles.suggestionItem, opacity: isAdded ? 0.8 : 1 }} onMouseDown={() => handleProductSelect(p)}>
                                            {p.imageUrl ? (
                                                <img src={p.imageUrl} alt="" style={{ width: '30px', height: '30px', borderRadius: '4px', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ width: '30px', height: '30px', borderRadius: '4px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px' }}>📦</div>
                                            )}
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: '500' }}>{p.name}</div>
                                                <div style={{ fontSize: '11px', color: '#10b981' }}>Stok: {p.quantity || 0} adet</div>
                                            </div>
                                            {isAdded && (
                                                <div style={{ 
                                                    fontSize: '10px', 
                                                    background: '#d1fae5', 
                                                    color: '#065f46', 
                                                    padding: '2px 8px', 
                                                    borderRadius: '12px', 
                                                    fontWeight: '700',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    ✓ Eklendi
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Miktar</label>
                        <input style={styles.input} type="number" min="1" value={currentItem.quantity} onChange={e => setCurrentItem(prev => ({ ...prev, quantity: Number(e.target.value) }))} />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Çıkış Deposu</label>
                        <select 
                            style={styles.select}
                            value={currentItem.warehouseId}
                            onChange={e => setCurrentItem(prev => ({ ...prev, warehouseId: e.target.value }))}
                        >
                            <option value="">Depo Seçin</option>
                            {warehouses.filter(w => w.isActive).map(w => (
                                <option key={w.id} value={w.id}>{w.name}</option>
                            ))}
                        </select>
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Birim Fiyat (₺)</label>
                        <input style={styles.input} type="number" value={currentItem.price} onChange={e => setCurrentItem(prev => ({ ...prev, price: Number(e.target.value) }))} />
                    </div>
                    <button style={{ ...styles.button, ...styles.addButton }} onClick={handleAddItem}>+ Listeye Ekle</button>
                </div>

                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th} width="60">Görsel</th>
                                <th style={styles.th}>Ürün</th>
                                <th style={styles.th}>Depo</th>
                                <th style={styles.th}>Miktar</th>
                                <th style={styles.th}>Birim Fiyat</th>
                                <th style={styles.th}>Toplam</th>
                                <th style={styles.th} width="50">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.map((item, index) => (
                                <tr key={index}>
                                    <td style={styles.td}>
                                        {item.imageUrl ? (
                                            <img src={item.imageUrl} alt="" style={styles.productImg} onError={e => { e.target.style.display = 'none'; }} />
                                        ) : (
                                            <div style={styles.placeholderImg}>🖼️</div>
                                        )}
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: '500' }}>{item.productName}</td>
                                    <td style={styles.td}>
                                        {item.warehouseId 
                                            ? warehouses.find(w => w.id === item.warehouseId)?.name || 'Bilinmiyor'
                                            : '-'}
                                    </td>
                                    <td style={styles.td}>
                                        <input
                                            type="number"
                                            style={styles.tableInput}
                                            value={item.quantity}
                                            onChange={e => updateLineItem(index, 'quantity', e.target.value)}
                                        />
                                    </td>
                                    <td style={styles.td}>
                                        <input
                                            type="number"
                                            style={styles.tableInput}
                                            value={item.price}
                                            onChange={e => updateLineItem(index, 'price', e.target.value)}
                                        /> ₺
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: '600', color: '#1e293b' }}>{item.total.toFixed(2)} ₺</td>
                                    <td style={styles.td}>
                                        <button style={styles.deleteButton} onClick={() => setLineItems(prev => prev.filter((_, i) => i !== index))}>🗑️</button>
                                    </td>
                                </tr>
                            ))}
                            {lineItems.length === 0 && (
                                <tr><td colSpan="6" style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '15px' }}>
                                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
                                    Henüz ürün eklenmedi. Listeyi doldurmak için yukarıdaki formu kullanın.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
                    <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '16px', minWidth: '280px', flex: 1 }}>
                        <h4 style={{ margin: 0, fontSize: '15px', color: '#475569' }}>Fatura Ayarları</h4>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                            Sevkiyat oluşturulduğunda sistem otomatik olarak satış faturası kesecektir. Lütfen KDV oranını belirleyin.
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>KDV Oranı (%)</label>
                            <input
                                type="number"
                                style={{ ...styles.input, width: '120px' }}
                                value={vatRatePercent}
                                onChange={e => {
                                    const val = parseInt(e.target.value);
                                    if (!isNaN(val) && val >= 0 && val <= 100) setVatRatePercent(val);
                                    else if (e.target.value === '') setVatRatePercent('');
                                }}
                            />
                        </div>
                        <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>
                            İşlem onaylandığında stoklar otomatik olarak düşülecektir.
                        </p>
                    </div>

                    <div style={{ width: '360px', background: '#1e293b', padding: '24px', borderRadius: '16px', color: 'white' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', fontSize: '14px', color: '#94a3b8' }}>
                            <span>Toplam Ürün Miktarı:</span>
                            <span style={{ color: '#10b981', fontWeight: '600' }}>{lineItems.reduce((acc, item) => acc + item.quantity, 0)} Adet</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', fontSize: '14px', color: '#94a3b8' }}>
                            <span>Ara Toplam:</span>
                            <span style={{ color: 'white' }}>{lineItems.reduce((acc, item) => acc + item.total, 0).toFixed(2)} ₺</span>
                        </div>
                        {createInvoice && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', fontSize: '14px', color: '#94a3b8' }}>
                                <span>KDV (%{vatRatePercent || 0}):</span>
                                <span style={{ color: 'white' }}>{(lineItems.reduce((acc, item) => acc + item.total, 0) * ((vatRatePercent || 0) / 100)).toFixed(2)} ₺</span>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px', paddingTop: '16px', borderTop: '1px solid #334155', fontSize: '20px', fontWeight: '800' }}>
                            <span>TOPLAM:</span>
                            <span style={{ color: '#10b981' }}>
                                {createInvoice
                                    ? (lineItems.reduce((acc, item) => acc + item.total, 0) * (1 + (vatRatePercent || 0) / 100)).toFixed(2)
                                    : lineItems.reduce((acc, item) => acc + item.total, 0).toFixed(2)} ₺
                            </span>
                        </div>
                        <button style={{ ...styles.button, ...styles.primaryButton, width: '100%', padding: '14px' }} onClick={handleSave}>
                            {createInvoice ? '✓ İrsaliye ve Fatura Kaydet' : '✓ İrsaliyeyi Kaydet'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default NewDirectShipment;
